import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../i18n';
import { useBodyScrollLock } from '../hooks';
import { analytics } from '../analytics';

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// 온보딩 단계 정의
const ONBOARDING_STEPS = [
  {
    id: 1,
    icon: '🚀',
    color: 'var(--neo-mint)',
  },
  {
    id: 2,
    icon: '📋',
    color: 'var(--neo-yellow)',
  },
  {
    id: 3,
    icon: '⚡',
    color: 'var(--neo-pink)',
  },
  {
    id: 4,
    icon: '✅',
    color: 'var(--neo-blue)',
  },
];

export default function OnboardingModal({ isOpen, onClose }: OnboardingModalProps) {
  const { lang } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');

  // 터치/스와이프 지원을 위한 상태
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentStep < ONBOARDING_STEPS.length - 1) {
      handleNext();
    }
    if (isRightSwipe && currentStep > 0) {
      handlePrev();
    }
  };

  const handleNext = useCallback(() => {
    if (currentStep < ONBOARDING_STEPS.length - 1 && !isAnimating) {
      setIsAnimating(true);
      setSlideDirection('left');
      setTimeout(() => {
        setCurrentStep((prev) => prev + 1);
        setIsAnimating(false);
      }, 200);
    }
  }, [currentStep, isAnimating]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0 && !isAnimating) {
      setIsAnimating(true);
      setSlideDirection('right');
      setTimeout(() => {
        setCurrentStep((prev) => prev - 1);
        setIsAnimating(false);
      }, 200);
    }
  }, [currentStep, isAnimating]);

  const handleSkip = () => {
    analytics.onboardingSkip(currentStep + 1);
    onClose();
  };

  const handleComplete = () => {
    analytics.onboardingComplete();
    onClose();
  };

  // 키보드 네비게이션
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleNext, handlePrev, onClose]);

  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const step = ONBOARDING_STEPS[currentStep];
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;

  // 온보딩 콘텐츠 (언어별)
  const getStepContent = (stepId: number) => {
    const content = {
      1: {
        title: lang === 'ko' ? 'ReleaseGate에 오신 것을 환영합니다!' : 'Welcome to ReleaseGate!',
        description: lang === 'ko' 
          ? 'AI 기반 릴리즈 판단 도구로\n출시 결정의 불확실성을 줄여보세요.' 
          : 'Reduce uncertainty in your release decisions\nwith our AI-powered release evaluation tool.',
      },
      2: {
        title: lang === 'ko' ? '기획 의도와 리스크 분석' : 'Planning & Risk Analysis',
        description: lang === 'ko'
          ? '기획 의도를 입력하고 예상되는 리스크를\n함께 분석하여 출시 준비도를 평가합니다.'
          : 'Input your planning intent and analyze\npotential risks to evaluate release readiness.',
      },
      3: {
        title: lang === 'ko' ? 'QA 검증 상태 점검' : 'QA Verification Check',
        description: lang === 'ko'
          ? '핵심 시나리오와 엣지 케이스의\n테스트 결과를 입력하세요.'
          : 'Enter test results for core scenarios\nand edge cases.',
      },
      4: {
        title: lang === 'ko' ? 'AI가 분석한 릴리즈 판단' : 'AI-Powered Release Decision',
        description: lang === 'ko'
          ? 'GO / CONDITIONAL / HOLDING / NO-GO\n명확한 판단과 권고사항을 받아보세요.'
          : 'Get clear decisions: GO / CONDITIONAL /\nHOLDING / NO-GO with recommendations.',
      },
    };
    return content[stepId as keyof typeof content];
  };

  const stepContent = getStepContent(step.id);

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      {/* 디밍 배경 */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={handleSkip}
      />
      
      {/* 모달 컨테이너 */}
      <div 
        className="relative z-10 w-full max-w-md mx-4 animate-in zoom-in-95 fade-in duration-300"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* 카드 */}
        <div className="neo-card-static bg-white dark:bg-[#1a1a2e] overflow-hidden">
          {/* 상단 컬러 바 */}
          <div 
            className="h-2 transition-all duration-300"
            style={{ backgroundColor: step.color }}
          />
          
          {/* 스킵 버튼 */}
          {!isLastStep && (
            <button
              onClick={handleSkip}
              className="absolute top-5 right-4 text-xs font-bold text-black/40 dark:text-white/40 hover:text-black/70 dark:hover:text-white/70 transition-colors z-10"
            >
              {lang === 'ko' ? '건너뛰기' : 'Skip'}
            </button>
          )}
          
          {/* 콘텐츠 영역 */}
          <div className="p-6 pt-8">
            {/* 아이콘 */}
            <div 
              className={`w-20 h-20 mx-auto mb-6 flex items-center justify-center transition-all duration-200 ${
                isAnimating 
                  ? slideDirection === 'left' 
                    ? 'translate-x-[-20px] opacity-0' 
                    : 'translate-x-[20px] opacity-0'
                  : 'translate-x-0 opacity-100'
              }`}
              style={{ backgroundColor: step.color }}
            >
              <span className="text-4xl">{step.icon}</span>
            </div>
            
            {/* 텍스트 */}
            <div 
              className={`text-center transition-all duration-200 ${
                isAnimating 
                  ? slideDirection === 'left' 
                    ? 'translate-x-[-30px] opacity-0' 
                    : 'translate-x-[30px] opacity-0'
                  : 'translate-x-0 opacity-100'
              }`}
            >
              <h2 
                id="onboarding-title"
                className="text-xl font-black text-black dark:text-white mb-3"
              >
                {stepContent.title}
              </h2>
              <p className="text-sm text-black/70 dark:text-white/70 whitespace-pre-line leading-relaxed">
                {stepContent.description}
              </p>
            </div>
          </div>
          
          {/* 하단 네비게이션 */}
          <div className="p-6 pt-4">
            {/* 단계 인디케이터 */}
            <div className="flex justify-center gap-2 mb-6">
              {ONBOARDING_STEPS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (!isAnimating) {
                      setSlideDirection(index > currentStep ? 'left' : 'right');
                      setIsAnimating(true);
                      setTimeout(() => {
                        setCurrentStep(index);
                        setIsAnimating(false);
                      }, 200);
                    }
                  }}
                  className={`w-2.5 h-2.5 transition-all duration-200 ${
                    index === currentStep
                      ? 'w-8 bg-black dark:bg-white'
                      : 'bg-black/20 dark:bg-white/20 hover:bg-black/40 dark:hover:bg-white/40'
                  }`}
                  aria-label={`${lang === 'ko' ? '단계' : 'Step'} ${index + 1}`}
                  aria-current={index === currentStep ? 'step' : undefined}
                />
              ))}
            </div>
            
            {/* 버튼 */}
            <div className="flex gap-3">
              {/* 이전 버튼 */}
              {currentStep > 0 && (
                <button
                  onClick={handlePrev}
                  disabled={isAnimating}
                  className="flex-1 py-3 neo-btn bg-white dark:bg-[#2d3a4f] text-black dark:text-white text-sm disabled:opacity-50"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                    {lang === 'ko' ? '이전' : 'Prev'}
                  </span>
                </button>
              )}
              
              {/* 다음/시작 버튼 */}
              <button
                onClick={isLastStep ? handleComplete : handleNext}
                disabled={isAnimating}
                className={`flex-1 py-3 neo-btn text-black text-sm font-bold disabled:opacity-50 ${
                  isLastStep 
                    ? 'bg-[var(--neo-mint)]' 
                    : 'bg-[var(--neo-lime)]'
                }`}
                style={!isLastStep ? { backgroundColor: step.color } : undefined}
              >
                <span className="flex items-center justify-center gap-2">
                  {isLastStep ? (
                    <>
                      {lang === 'ko' ? '시작하기' : 'Get Started'}
                      <span>🎉</span>
                    </>
                  ) : (
                    <>
                      {lang === 'ko' ? '다음' : 'Next'}
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>
        
        {/* 좌우 화살표 (데스크탑) */}
        <div className="hidden sm:block">
          {currentStep > 0 && (
            <button
              onClick={handlePrev}
              disabled={isAnimating}
              className="absolute left-[-60px] top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors disabled:opacity-30"
              aria-label={lang === 'ko' ? '이전 단계' : 'Previous step'}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {!isLastStep && (
            <button
              onClick={handleNext}
              disabled={isAnimating}
              className="absolute right-[-60px] top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors disabled:opacity-30"
              aria-label={lang === 'ko' ? '다음 단계' : 'Next step'}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

